import type { DocEntityHandle } from "building-types/handles/docEntityHandleTypes";
import type { Modify } from "common/utils/types";
import type { DataSequenceUnit } from "./dataSequenceUnitTypes";
import type { SystemDataSequenceEntityType } from "./systemDataSequenceTypes";

export type KeyAttribute = {
  readonly value: string;
  readonly unit?: DataSequenceUnit;
};
export type KeyAttributeOptions = {
  readonly definesEntity: boolean;
  readonly unit?: DataSequenceUnit;
  readonly isHidden?: boolean;
};
export type KeyAttributeOptionsArgs = Partial<Modify<KeyAttributeOptions, { unit: DataSequenceUnit }>>;

export enum AggregationMethod {
  SUM = "SUM",
  TAKEFIRST = "TAKEFIRST",
}

export type KeyMeasure = {
  readonly value: number;
  readonly baseValue: number;
  readonly unit: DataSequenceUnit;
};

export type KeyMeasureOptions = {
  readonly unit: DataSequenceUnit;
  readonly isAggregated: boolean;
  readonly isHidden?: boolean;
  readonly aggregationMethod: AggregationMethod;
};

export type KeyMeasureOptionsArgs = Partial<Modify<KeyMeasureOptions, { unit: DataSequenceUnit }>>;

export type EntityKeyAttributes = Record<string, KeyAttribute>;
export type EntityKeyMeasures = Record<string, KeyMeasure>;
export type EntityKeyAttributeRecord = Modify<KeyAttributeOptions, { key: keyof EntityKeyAttributes }>;

export type Attributes = <Attributes extends EntityKeyAttributes>(attributes: Attributes) => Attributes;
export type Measures = <Measures extends EntityKeyMeasures>(measures: Measures) => Measures;

export type KeyAttributesAndMeasures<Attributes, Measures> = {
  readonly type: SystemDataSequenceEntityType;
  readonly attributes: Attributes;
  readonly measures: Measures;
  /** The entities this row was derived from, used as the row's identifier column. */
  readonly handles: DocEntityHandle[];
};

export type AggregatedKeyAttributesAndMeasures<
  Attributes extends EntityKeyAttributes,
  Measures extends EntityKeyMeasures,
> = KeyAttributesAndMeasures<Attributes, Measures> & {
  readonly definingAttributesHash: string;
};

export type KeyAttributesAndMeasuresOptions<
  Attributes extends EntityKeyAttributes,
  Measures extends EntityKeyMeasures,
> = {
  readonly attributes: Partial<Record<keyof Attributes, KeyAttributeOptions>>;
  readonly measures: Partial<Record<keyof Measures, KeyMeasureOptions>>;
};

export type KAMColumnDisplayOptions = {
  readonly hidden?: boolean;
  readonly header?: string;
};

export type KeyAttributesAndMeasuresDisplayOptions<
  Attributes extends EntityKeyAttributes,
  Measures extends EntityKeyMeasures,
> = {
  readonly attributes?: Partial<Record<keyof Attributes, KAMColumnDisplayOptions>>;
  readonly measures?: Partial<Record<keyof Measures, KAMColumnDisplayOptions>>;
};

export type KeyAttributesAndMeasuresSortOrder<
  Attributes extends EntityKeyAttributes,
  Measures extends EntityKeyMeasures,
> = (a: KeyAttributesAndMeasures<Attributes, Measures>, b: KeyAttributesAndMeasures<Attributes, Measures>) => number;

export type ProcureBuildingOptionMeasures = {
  readonly count: KeyMeasure;
};

export type BuildingOptionKeyAttributes = {
  readonly optionKey: KeyAttribute;
  readonly description: KeyAttribute;
  readonly category: KeyAttribute;
  readonly salesCategory: KeyAttribute;
  readonly optionSet: KeyAttribute;
  readonly optionSetDescription: KeyAttribute;
  readonly name: KeyAttribute;
  readonly entityName: KeyAttribute;
  readonly builderOptionSetCode: KeyAttribute;
  readonly optionCode: KeyAttribute;
  readonly builderTemplateCode: KeyAttribute;
  readonly price: KeyAttribute;
};

export type BuildingAreaKeyMeasures = {
  readonly area: KeyMeasure;
  readonly count: KeyMeasure;
};

export type BuildingAreaKeyAttributes = {
  readonly category: KeyAttribute;
  readonly name: KeyAttribute;
  readonly entityName: KeyAttribute;
};
export type BuildingAreaKeyMeasure = {
  readonly category: string;
  readonly name: string;
  readonly area: number;
  readonly handles: DocEntityHandle[];
};

export type BuildingAreaKAM = KeyAttributesAndMeasures<BuildingAreaKeyAttributes, BuildingAreaKeyMeasures>;
export type BuildingAreaKAMOptions = KeyAttributesAndMeasuresOptions<
  BuildingAreaKeyAttributes,
  BuildingAreaKeyMeasures
>;

export type ProcureBuildingPreset = {
  readonly presetId: string;
  readonly name: string;
  readonly optionChoices: string;
  readonly price: number;
  readonly builderOptionCode: string;
  readonly handles: DocEntityHandle[];
};

export type ProcureBuildingOption = {
  readonly optionKey: string;
  readonly description: string | undefined;
  readonly category: string | undefined;
  readonly salesCategory: string | undefined;
  readonly optionSetName: string;
  readonly optionSetDescription: string | undefined;
  readonly optionName: string;
  readonly price: string | undefined;
  readonly builderOptionCode: string | undefined;
  readonly builderOptionSetCode: string | undefined;
  readonly builderTemplateCode: string | undefined;
  readonly handles: DocEntityHandle[];
};

export type BuildingSubEntity = "BUILDING_AREA" | "BUILDING_OPTION" | "BUILDING_PRESET";

export type BuildingOptionKAM = KeyAttributesAndMeasures<BuildingOptionKeyAttributes, ProcureBuildingOptionMeasures>;
export type BuildingOptionKAMOptions = KeyAttributesAndMeasuresOptions<
  BuildingOptionKeyAttributes,
  ProcureBuildingOptionMeasures
>;

export type BuildingPresetMeasures = {
  readonly count: KeyMeasure;
};

export type BuildingPresetKeyAttributes = {
  readonly presetId: KeyAttribute;
  readonly name: KeyAttribute;
  readonly optionChoices: KeyAttribute;
  readonly builderOptionCode: KeyAttribute;
  readonly entityName: KeyAttribute;
};

export type BuildingPresetKAM = KeyAttributesAndMeasures<BuildingPresetKeyAttributes, BuildingPresetMeasures>;
export type BuildingPresetKAMOptions = KeyAttributesAndMeasuresOptions<
  BuildingPresetKeyAttributes,
  BuildingPresetMeasures
>;
